import { useMemo, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, IconButton, SegmentedControl } from '../ui/index.ts';
import { clock } from '../game/format.ts';
import styles from './Replay.module.css';
import { REPLAY_SPEEDS, type ReplayClient, type ReplaySpeed } from './replayClient.ts';

/**
 * What a replay adds to the screens of the game: a strip at the foot of the page to play and pause,
 * to move in time, to go faster, and to watch from another seat. The game above it is the same, read
 * through the view of the seat that is watched.
 */
export function ReplayLayer({ client }: { client: ReplayClient }) {
  const { t } = useTranslation('meta');
  const subscribe = useMemo(() => (l: () => void) => client.subscribe(l), [client]);
  useSyncExternalStore(subscribe, () => client.version());

  return (
    <>
      <div className={styles['tag']}>
        <Badge tone="amber">{t('replay.tag')}</Badge>
      </div>
      <section className={styles['bar']} aria-label={t('replay.controls')}>
        <IconButton
          icon={client.playing ? 'pause' : 'play'}
          label={client.playing ? t('replay.pause') : t('replay.play')}
          onClick={() => {
            client.setPlaying(!client.playing);
          }}
        />
        <div className={styles['scrub']}>
          <input
            type="range"
            className={styles['range']}
            aria-label={t('replay.time')}
            min={0}
            max={Math.max(1, Math.round(client.durationMs / 1000))}
            step={1}
            value={Math.round(client.timeMs / 1000)}
            aria-valuetext={`${clock(client.timeMs)} / ${clock(client.durationMs)}`}
            onChange={(e) => {
              client.seekTime(Number(e.target.value) * 1000);
            }}
          />
          <span className={styles['time']}>
            {`${clock(client.timeMs)} / ${clock(client.durationMs)}`}
          </span>
        </div>
        <SegmentedControl<ReplaySpeed>
          label={t('replay.speed')}
          hideLabel
          value={client.speedFactor}
          onChange={(v) => {
            client.setSpeedFactor(v);
          }}
          options={REPLAY_SPEEDS.map((s) => ({ value: s, label: `${String(s)}×` }))}
        />
        <label className={styles['seat']}>
          <span>{t('replay.watchAs')}</span>
          <select
            value={client.watching}
            onChange={(e) => {
              client.watchFrom(e.target.value);
            }}
          >
            {client.config.players.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </label>
      </section>
    </>
  );
}

/** The room the strip takes at the foot of the page, so that nothing hides behind it. */
export function ReplaySpace() {
  return <div className={styles['space']} aria-hidden="true" />;
}
